import { z } from 'zod';

/**
 * Modello dei dati su misura (ADR 0005): i tipi di cose che l'azienda gestisce, ricavati dal motore, con le loro
 * entità nell'archivio. Ogni tipo ha l'attributo `nome`, che distingue un'entità dalle altre.
 */
/** I vincoli di un numero o di una quantità in una sua unità (ADR 0249 § 1). */
export interface VincoliNumero {
  intero?: boolean;
  decimali?: number;
  min?: number;
  max?: number;
  /** Il valore va a passi di questo (0,5 per i vani). */
  passo?: number;
}

/** I vincoli di un campo (ADR 0249 § 1): il codice conosce i tipi di vincolo, la libreria dice quali valgono per il campo. */
export interface VincoliCampo extends VincoliNumero {
  /** Per una quantità: vincoli diversi per unità (chiave della voce dell'elenco delle unità). */
  per_unita?: Record<string, VincoliNumero>;
  /** Lo schema di un codice (packages/standards/src/codici.ts). */
  schema?: string;
  /** I caratteri ammessi in un codice senza schema. */
  caratteri?: 'cifre' | 'lettere' | 'lettere_cifre' | 'codice';
  /** Lunghezza massima di un testo o di un codice. */
  lunghezza?: number;
  /** Una data o un anno nel passato o nel futuro (oggi compreso). */
  tempo?: 'passato' | 'futuro';
  /** La data viene dopo (o il giorno stesso) della data di un altro campo della voce, o prima. */
  dopo?: string;
  prima?: string;
}

export interface DataAttributeDto {
  nome: string;
  etichetta: string;
  tipo: 'testo' | 'numero' | 'data';
  descrizione: string | null;
  /** Aggiunto da una lettura (ADR 0195 § 6): con il segno finché la persona non lo accetta. */
  aggiunto?: { il: string; fonte: string; accettato_il?: string | null };
  /** Cosa dipende da questo attributo (un obbligo, un documento, una procedura). */
  perche: string | null;
  /**
   * Legame con un'altra cosa dell'azienda: chiave di un altro tipo del modello, o "persona" per le persone che ci
   * lavorano. Il valore è il nome dell'entità collegata (più nomi separati da "; " se `multiplo`).
   */
  riferisce?: string | null;
  multiplo?: boolean;
  /** Il valore è un indirizzo postale: si salva come lo completa Google (ADR 0074). */
  indirizzo?: boolean;
  /**
   * Come si scrive il valore (ADR 0192), da un elenco fisso del sistema: testo, numero, importo, data, iban,
   * codice_fiscale, partita_iva, provincia, cap, comune, telefono, email, web, indirizzo, catastale.
   */
  formato?: string;
  /** L'attributo identifica una voce: due voci con lo stesso valore sono la stessa cosa (ADR 0192). */
  identita?: boolean;
  /** Il dato che una cosa di questo tipo ha sempre, dalla libreria (ADR 0229): una voce letta che non ne ha nessuno non è del tipo. */
  obbligatorio?: boolean;
  /**
   * Come si risponde (ADR 0208): testo, testo_lungo, codice, numero, quantita, importo, si_no, scelta, scelte, data, ora,
   * data_ora, anno, periodo, esercizio, indirizzo, luogo, allegato, collegamento. Senza forma vale il formato.
   */
  forma?: string;
  /** La chiave dell'elenco delle voci (scelta, scelte) o delle unità (quantita), in data/elenchi-campi.json. */
  elenco?: string;
  /** Il campo della stessa entità che filtra le voci dell'elenco. */
  dipende_da?: string;
  /** L'unità fissa di un numero (m², kW, ‰, mesi). */
  unita?: string;
  /**
   * I vincoli del valore (ADR 0249 § 1), dalla libreria: intero o decimali, minimo, massimo e passo (anche per unità),
   * lo schema o i caratteri di un codice, la lunghezza di un testo, dove sta una data nel tempo.
   */
  vincoli?: VincoliCampo;
  /** Per un collegamento: la chiave del tipo dell'archivio collegato. */
  verso?: string;
  /** Una spiegazione per i casi non ovvi (l'unità che cambia, il prezzo «per unità di»). */
  nota?: string;
}

/**
 * La soglia di pertinenza (ADR 0248 § 2): da qui in su una voce, un campo o un tipo letti entrano come dati, senza segno
 * e senza domanda; sotto portano il segno «da confermare».
 */
export const CERTAIN_RELEVANCE = 90;

export interface DataEntityDto {
  /** Riferimento stabile dell'entità. */
  rif: string;
  nome: string;
  /**
   * Aggiunta da una lettura (ADR 0195 § 6) e non ancora accettata: quando e da dove. Dall'ADR 0248 solo sotto la soglia
   * di pertinenza, con la pertinenza e i controlli che l'hanno abbassata.
   */
  aggiunta?: { il: string; fonte: string; pertinenza?: number; motivi?: string[] } | null;
  attributi: Record<string, string | number | null>;
  /** Da dove viene ogni attributo ("persona" se l'ha scritto la persona). */
  fonti: Record<string, string>;
  /** Attributi del tipo ancora senza valore. */
  mancanti: string[];
  /** Sedi e attività economiche con cui la cosa ha a che fare (ADR 0057). */
  legami: DataEntityLinkDto[];
  /** Nessuna sede, o una sede con l'attività ancora da attribuire. */
  da_attribuire: boolean;
  /** Altri nomi con cui è stata trovata (doppioni uniti, ADR 0058). */
  altri_nomi: string[];
  /** Le voci a cui è legata secondo i legami del suo tipo: significato del legame → nome della voce (ADR 0216). */
  voci_legate?: Record<string, string>;
}

/**
 * Legame di un'entità (ADR 0057): una sede (null = l'impresa come soggetto) e un'attività economica di quella sede
 * (null = da attribuire). `escluso` = la persona ha detto di no, nessuna lettura lo rimette.
 */
export interface DataEntityLinkDto {
  sede_id: string | null;
  sede: string | null;
  attivita_id: string | null;
  attivita: string | null;
  stato: 'attivo' | 'escluso';
  deciso_da: 'motore' | 'dedotto' | 'regola' | 'persona';
  fonte: string;
  motivo: string | null;
}

export interface DataTypeDto {
  chiave: string;
  etichetta: string;
  descrizione: string | null;
  perche: string;
  /** Aggiunto da una lettura (ADR 0195 § 6): le prove e, se accettato, quando. */
  aggiunto?: { il: string; prove: string[]; accettato_il?: string | null } | null;
  attributi: DataAttributeDto[];
  /** unito = entrato in un altro tipo (ADR 0168): non si ripropone. */
  stato: 'attivo' | 'non_serve' | 'unito';
  /** Per un tipo unito: il tipo in cui è entrato. */
  unito_in: string | null;
  /** Unione proposta dal controllo dei doppioni, da confermare (ADR 0168). */
  unione_proposta: { in: string; perche: string } | null;
  /** Il ruolo tra i documenti dei rapporti (ADR 0168, 0169), es. fattura_acquisto; null per gli altri tipi. */
  ruolo_documento: 'fattura_vendita' | 'fattura_acquisto' | 'contratto_vendita' | 'contratto_acquisto' | null;
  entita: DataEntityDto[];
  /** La persona ha confermato che l'elenco è completo (null finché non lo dice). */
  completo_il: string | null;
  /** Le sue entità sono prodotti con istruzioni del produttore (manuale, manutenzione) da cercare online. */
  info_produttore: boolean;
  /** Le sue entità sono soggetti esterni: hanno P.IVA e codice fiscale e si uniscono i doppioni (ADR 0058). */
  soggetto_esterno: boolean;
  /** Forma del tipo (ADR 0195 § 4); assente o null per i tipi nati prima della ricostruzione. */
  ruolo?: DataTypeRole | null;
  origine?: DataTypeOrigin | null;
  /** Per un tipo di uno strato: la chiave della voce nello strato. */
  voce_strato?: string | null;
  /** Le fasi, procedure, obblighi o controlli a cui serve. */
  serve_a?: string[];
  /** La condizione dell'azienda per cui vale (null = sempre). */
  condizione?: string | null;
  legami_tipo?: DataTypeLinkDto[];
  stati?: DataTypeStateDto[];
  eventi?: DataTypeEventDto[];
}

/** Ruolo di forma di un tipo (ADR 0193 § 2): non è una categoria del contenuto, dice solo la forma. */
export type DataTypeRole = 'cosa' | 'soggetto' | 'rapporto' | 'evento' | 'riferimento';

/** Da dove viene un tipo (ADR 0195 § 4). */
export type DataTypeOrigin = 'strato' | 'programma' | 'lettura' | 'persona';

/** Legame tipizzato verso un altro tipo del modello (ADR 0195 § 4). */
export interface DataTypeLinkDto {
  /** Chiave dell'altro tipo. */
  tipo: string;
  /** Possiede, si trova in, è parte di, regola, è prova di… */
  significato: string;
  /** Quante entità dell'altro tipo per una di questo. */
  cardinalita: 'uno' | 'molti';
  /** Il legame ha una quota (es. la percentuale di titolarità). */
  quota: boolean;
  /** Il legame vale per un periodo (dal… al…). */
  periodo: boolean;
  /** Se il legame deve esserci (ADR 0238); assente nei tipi di prima, che valgono «facoltativo». */
  obbligo?: 'genitore' | 'obbligatorio' | 'alternativa' | 'facoltativo';
  /** L'alternativa, o il genitore tra più tipi, di cui fa parte. */
  gruppo?: string;
}

export interface DataTypeStateDto {
  stato: string;
  /** Gli stati in cui può passare. */
  verso: string[];
}

/** Evento ricorrente del tipo (es. il canone mensile di un contratto di locazione). */
export interface DataTypeEventDto {
  /** Chiave del tipo dell'evento. */
  tipo: string;
  /** mensile, trimestrale, annuale, alla scadenza…; null se non ha una cadenza fissa. */
  cadenza: string | null;
}

/** Una condizione dell'azienda (ADR 0195 § 3): in potenza per tutte, risolta dai dati con la prova. */
export interface CompanyConditionDto {
  chiave: string;
  etichetta: string;
  stato: 'da_verificare' | 'si' | 'no';
  prove: Array<{ citazione: string; fonte: string }>;
  deciso_da: 'regola' | 'motore' | 'persona' | null;
  deciso_il: string | null;
  /** Vale per l'azienda: sì, oppure da verificare con l'ipotesi più ampia (ADR 0195 § 3, decisione del 2026-10-27). */
  vale: boolean;
  /** Vale per ipotesi: le sue voci sono «da confermare» e la sua domanda compare nelle sezioni che governa. */
  da_confermare: boolean;
  /** Le sezioni che governa: chiavi delle entità e dei documenti attesi degli strati (s2.ent.…, s3.doc.…). */
  sezioni: string[];
}

/** Le condizioni dell'azienda (ADR 0195 § 3): quante sono ancora da confermare. Nessuna domanda all'iscrizione. */
export interface CompanyConditionsState {
  data: CompanyConditionDto[];
  da_confermare: number;
}

/** Le domande di una sezione del gestionale (pop-up quando il titolare la apre): solo le condizioni da confermare che la governano. */
export interface SectionQuestions {
  sezione: string;
  domande: Array<{ chiave: string; etichetta: string; descrizione: string; dove_cercare: string[]; altre_sezioni: number }>;
}

/** Le sezioni del gestionale composto dagli strati (ADR 0195 § 7). */
export interface SectionType {
  key: string;
  label: string;
  /** La chiave della sezione per le domande (la voce dello strato): `GET …/conditions/sezione/:sezione`. */
  sezione: string;
  ruolo: string | null;
  condizione: string | null;
  /** La sua condizione vale per ipotesi: da confermare nella sezione. */
  da_confermare: boolean;
  voci: number;
  /** Tipo atteso ancora senza voci (ADR 0195 § 7): le voci si raccolgono dai programmi, dalla posta e dai documenti. */
  da_raccogliere: boolean;
  /** L'area funzionale del menu in cui sta (ADR 0202); null per un tipo senza area nella libreria. */
  area: string | null;
  /** La voce dello strato della testata di cui è una voce (ADR 0203): si vede nella scheda della testata, non nel menu. */
  parte_di: string | null;
}

/**
 * Un'area funzionale del menu del gestionale (ADR 0202), dalla libreria: prima quelle del mestiere, poi quelle comuni.
 * Le viste sono il soggetto filtrato per un ruolo (Clienti, Fornitori, Inquilini…).
 */
export interface MenuAreaDto {
  chiave: string;
  nome: string;
  /** Il nome di un'icona del sistema, scelta dalla libreria. */
  icona: string;
  mestiere: boolean;
  viste: Array<{ nome: string; ruolo: string }>;
}

export interface SectionDocument {
  id: string;
  title: string;
  sezione: string;
  condizione: string | null;
  da_confermare: boolean;
  file: number;
}

export interface SectionsState {
  /** Le aree del menu nell'ordine, anche quelle ancora vuote: il menu mostra quelle con qualcosa. */
  aree: MenuAreaDto[];
  tipi: SectionType[];
  documenti: SectionDocument[];
  /** Tipi e documenti venuti dagli strati e tolti: ognuno con il motivo, così niente sparisce senza traccia. */
  tolti: Array<{ key: string; label: string; motivo: string }>;
  da_confermare: number;
  da_raccogliere: number;
}

/**
 * Un elenco delle tendine per la pagina (ADR 0208): le voci della libreria e quelle aggiunte dall'azienda. `libero` = per
 * ora l'elenco non ha voci proprie (da costruire o in un'altra tabella) e il campo si scrive come testo.
 */
export interface FieldListDto {
  chiave: string;
  nome: string;
  ufficiale: boolean;
  estendibile: boolean;
  libero: boolean;
  /** Per un elenco dipendente: l'elenco del campo padre. */
  dipende_da: string | null;
  voci: Array<{ valore: string; etichetta: string; per: string[] | null; dell_azienda: boolean }>;
}

/** Una voce nuova con «Altro…» in un elenco estendibile; per un elenco dipendente, i valori del padre per cui vale. */
export const FieldListOptionSchema = z.object({
  etichetta: z.string().trim().min(1).max(120),
  per: z.array(z.string().trim().min(1).max(120)).max(50).optional(),
});
export type FieldListOption = z.infer<typeof FieldListOptionSchema>;

/** La risposta del titolare su una condizione: sì o no, con una nota se vuole. */
/** La risposta del titolare a una condizione; con `sede` vale solo per quella sede (ADR 0210 § 5). */
export const ConditionAnswerSchema = z.object({ stato: z.enum(['si', 'no']), nota: z.string().trim().max(500).optional(), sede: z.string().uuid().optional() });
export type ConditionAnswer = z.infer<typeof ConditionAnswerSchema>;

export interface DataModelState {
  data: DataTypeDto[];
  generating: boolean;
  generated_at: string | null;
  error: string | null;
  /** Serve sapere qualcosa dell'azienda (sezione Azienda) prima di ricavare il modello. */
  needs_profile: boolean;
  /** Tipi nati dopo documenti o posta già letti, da cercare in quelle letture (ADR 0149). */
  da_rileggere: Array<{ chiave: string; etichetta: string }>;
}

/** Un collegamento della scheda di un'entità con un'altra cosa (ADR 0059). */
export interface EntityRelationDto {
  /** uscente = un suo valore nomina l'altra; entrante = l'altra la nomina; stesso_soggetto = la stessa in un altro tipo. */
  verso: 'uscente' | 'entrante' | 'stesso_soggetto';
  /** L'attributo che fa da legame (etichetta); null per lo stesso soggetto. */
  attributo: string | null;
  /** Tipo dell'altra cosa ("persona" per le persone dell'azienda). */
  tipo: string;
  etichetta_tipo: string;
  /** Riferimento dell'altra entità (null se il nome non corrisponde a nessuna nota). */
  rif: string | null;
  nome: string;
}

/** Scheda di un'entità (ADR 0059): valori, relazioni, documenti e posta che la riguardano. */
export interface EntityCardDto {
  tipo: Pick<DataTypeDto, 'chiave' | 'etichetta' | 'descrizione' | 'attributi' | 'soggetto_esterno'>;
  entita: DataEntityDto;
  relazioni: EntityRelationDto[];
  /** Documenti, messaggi ed entità più vicini per significato (ADR 0073). */
  vicini: Array<{ fonte: 'documento' | 'entita'; id: string; titolo: string; tipo: string | null; etichetta: string | null; riferimento: string | null; somiglianza: number }>;
  /** Documenti del rapporto (ADR 0060), quelli da cui risultano i suoi valori (fonte) o legati a lei nell'archivio. */
  documenti: Array<{ id: string; titolo: string; tipo: string; data: string; perche: 'rapporto' | 'fonte' | 'collegato'; che_cosa: string | null; data_documento: string | null }>;
  /** Documenti che la nominano nel testo, con il passaggio. */
  citazioni: Array<{ id: string; titolo: string; tipo: string; data: string; passaggio: string | null }>;
  /** Messaggi di posta che la nominano; null per chi non vede la posta. */
  posta: Array<{
    rif: string;
    oggetto: string | null;
    da: string | null;
    data: string | null;
    casella: string | null;
    passaggio: string | null;
    /** Legato al rapporto (ADR 0060), con che cosa è; altrimenti lo nomina soltanto. */
    legato?: boolean;
    che_cosa?: string | null;
  }> | null;
  /** Con quali parole è stata cercata nei testi. */
  cercata_come: string[];
  /**
   * Il contenuto della fattura elettronica da cui viene (ADR 0216 § 7): righe e riepiloghi IVA restano dati del file e si
   * mostrano dalla scheda. Vuoto per una voce che non è una fattura elettronica.
   */
  fattura_elettronica?: Array<{
    documento_id: string;
    titolo: string;
    numero: string | null;
    divisa: string | null;
    righe: Array<{
      numero: number | null;
      descrizione: string | null;
      quantita: number | null;
      unita_misura: string | null;
      prezzo_unitario: number | null;
      prezzo_totale: number | null;
      aliquota_iva: number | null;
      natura: string | null;
    }>;
    riepiloghi: Array<{ aliquota_iva: number | null; natura: string | null; imponibile: number | null; imposta: number | null; esigibilita: string | null }>;
  }>;
  /**
   * Righe dei programmi collegati che la nominano (ADR 0120), un elenco per tipo del programma: le colonne legate al
   * suo tipo hanno il suo nome, un altro nome o un suo codice. Le più recenti, con il totale.
   */
  programmi: Array<{
    tipo: string;
    etichetta: string;
    /** Il programma che ne è master. */
    programma: string;
    colonne: Array<{ nome: string; etichetta: string }>;
    /** Le colonne che la nominano. */
    legata_da: string[];
    righe: Array<{ rif: string; valori: Record<string, unknown> }>;
    totale: number;
  }>;
}

/** Recupero dei documenti già letti (ADR 0060): stima senza motore, poi collegamento su conferma. */
export interface DocumentLinksStateDto {
  fase: 'nessuna' | 'stima' | 'pronto' | 'collego' | 'finito' | 'errore';
  stima: {
    documenti: number;
    messaggi: number;
    /** Documenti e messaggi che nominano troppi soggetti (registri, elenchi): non si legano. */
    esclusi: number;
    token: number;
    costo_usd: number;
  } | null;
  /** Avanzamento del collegamento. */
  fatti: number;
  totale: number;
  legami: number;
  nuove_voci: number;
  errori: number;
  errore: string | null;
  aggiornato_il: string | null;
}

/** Rilettura dei documenti e della posta già letti per i tipi nuovi (ADR 0149): stima senza motore, poi rilettura. */
export interface TypeRereadStateDto {
  /** fermo = interrotta (es. tetto di spesa raggiunto): riparte da dove era con un nuovo avvio. */
  fase: 'nessuna' | 'stima' | 'pronto' | 'rileggo' | 'fermo' | 'finito' | 'errore';
  stima: {
    tipi: Array<{ chiave: string; etichetta: string; documenti: number; messaggi: number }>;
    documenti: number;
    messaggi: number;
    token: number;
    costo_usd: number;
  } | null;
  /** Avanzamento della rilettura. */
  fatti: number;
  totale: number;
  /** Voci riconosciute nelle fonti rilette (nuove o già note). */
  voci: number;
  errori: number;
  errore: string | null;
  aggiornato_il: string | null;
}

/** Completamento dei valori mancanti (ADR 0073): stima, poi lettura delle fonti su conferma. */
export interface CompletionStateDto {
  fase: 'nessuna' | 'pronto' | 'completo' | 'finito' | 'errore';
  stima: {
    /** Voci con valori mancanti. */
    voci: number;
    valori_mancanti: number;
    token: number;
    /** Costo massimo: le voci senza fonti non costano. */
    costo_usd: number;
  } | null;
  fatti: number;
  totale: number;
  /** Voci senza fonti da leggere. */
  senza_fonti: number;
  valori: number;
  /** Voci spostate nel rapporto che le loro fonti mostrano (ADR 0102). */
  spostate: number;
  errori: number;
  errore: string | null;
  aggiornato_il: string | null;
}

/** La persona unisce entità dello stesso tipo che sono lo stesso soggetto (ADR 0058). */
/** La persona sposta una voce in un altro tipo (ADR 0168): valori, altri nomi, legami e documenti vanno con lei. */
/** La persona sposta più voci insieme in un altro tipo (ADR 0168). */
export const DataEntitiesMoveSchema = z.object({
  rif: z.array(z.string().min(1).max(200)).min(1).max(500).describe('Riferimenti delle voci da spostare'),
  in: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/).describe('Chiave del tipo in cui vanno'),
});

export const DataEntityMoveSchema = z.object({ in: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/).describe('Chiave del tipo in cui va la voce') });

export const DataEntityMergeSchema = z.object({
  tieni: z.string().min(1).max(200).describe('Riferimento dell’entità da tenere'),
  unisci: z.array(z.string().min(1).max(200)).min(1).max(50).describe('Riferimenti delle entità da unire a quella tenuta'),
});

/** Doppioni possibili di un tipo, da far confermare alla persona. */
export interface DataDuplicatesDto {
  tipo: string;
  possibili: Array<{ entita: Array<{ rif: string; nome: string; partita_iva: string | null }>; perche: string }>;
}

export const DataTypeStatusSchema = z.object({ stato: z.enum(['attivo', 'non_serve']) });

/** La persona conferma un'unione proposta, o ne sceglie un'altra; null = la rifiuta (ADR 0168). */
export const DataTypeMergeSchema = z.object({ in: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/).nullable() });

const MappingPairSchema = z.object({ attributo: z.string().max(63), colonna: z.string().max(200) });

/** La persona scrive la mappatura di una lettura di un programma (ADR 0168). */
export const ProgramMappingSchema = z.object({
  riga: z.object({
    cosa: z.enum(['voce', 'senza_tipo', 'niente']),
    tipo: z.string().max(63).nullable(),
    senza_tipo: z.object({ cosa: z.string().max(200), perche: z.string().max(300) }).nullable(),
    nome: z.string().max(200).nullable(),
    attributi: z.array(MappingPairSchema).max(60),
  }),
  cose: z.array(z.object({ tipo: z.string().max(63), nome: z.string().max(200), attributi: z.array(MappingPairSchema).max(60), legame: z.string().max(63).nullable() })).max(20),
  verso: z.enum(['vendita', 'acquisto', 'nessuno']),
  perche: z.string().max(300).default(''),
});

/** La persona corregge nome e descrizione di un tipo (ADR 0047): la generazione non li cambia più. */
export const DataTypeEditSchema = z.object({
  etichetta: z.string().trim().min(1).max(120),
  descrizione: z.string().trim().max(1000).nullable().default(null),
});

/** Entità scritta dalla persona: nuova (rif assente) o modificata; un valore null o vuoto non cancella. */
/** Sedi e attività di un'entità dette dalla persona: per ogni sede le attività (vuoto = da attribuire); `sede` null = l'impresa. */
export const EntityLinksSchema = z
  .array(
    z.object({
      sede: z.string().uuid().nullable().describe('Id della sede; null = il rapporto è con l’impresa come soggetto'),
      attivita: z.array(z.string().uuid()).max(50).default([]).describe('Id delle attività economiche di quella sede; vuoto = da attribuire'),
    }),
  )
  .max(100);
export type EntityLinksInput = z.infer<typeof EntityLinksSchema>;

export const DataEntitySchema = z.object({
  rif: z.string().min(1).max(200).nullable().default(null),
  attributi: z.record(z.string().max(2000).nullable()),
  /** Se c'è, sono tutti i legami dell'entità secondo la persona (ADR 0057). */
  legami: EntityLinksSchema.nullable().default(null),
});

/**
 * Una voce salvata dalla persona con le voci a cui si lega (ADR 0238, tappa 3): per significato del legame, il nome della
 * voce dell'altro tipo. Il genitore è richiesto.
 */
export const ModelEntitySchema = DataEntitySchema.extend({
  /** Più voci di un legame «molti» separate da «; »; vuoto toglie il legame (mai il genitore). */
  voci_legate: z.record(z.string().trim().max(2000)).default({}),
});
export type ModelEntityInput = z.infer<typeof ModelEntitySchema>;

/** Il genitore scelto dalla persona per una voce in attesa (ADR 0238): per significato del legame, il nome della voce. */
export const WaitingReleaseSchema = z.object({ voci_legate: z.record(z.string().trim().min(1).max(2000)) });

export const EntityLinksBodySchema = z.object({ legami: EntityLinksSchema });
export type DataEntityInput = z.infer<typeof DataEntitySchema>;

/** La persona dice se l'elenco di un tipo è completo (o non più). */
export const DataTypeCompleteSchema = z.object({ completo: z.boolean() });

/** Mappatura di una lettura di un programma collegato (ADR 0168): come le sue righe diventano cose dell'azienda. */
export interface ProgramMappingDto {
  /** Che cosa è ogni riga: una voce di un tipo del modello, una cosa che nessun tipo descrive, o niente. */
  riga: {
    cosa: 'voce' | 'senza_tipo' | 'niente';
    tipo: string | null;
    /** Per «senza_tipo»: il genere di cose e cosa ne dipende. */
    senza_tipo: { cosa: string; perche: string } | null;
    nome: string | null;
    attributi: Array<{ attributo: string; colonna: string }>;
  };
  /** Le altre cose dell'azienda che ogni riga nomina (la controparte, un bene, un contratto…). */
  cose: Array<{
    tipo: string;
    nome: string;
    attributi: Array<{ attributo: string; colonna: string }>;
    /** Attributo della voce della riga che punta a questa cosa (il legame), se c'è. */
    legame: string | null;
  }>;
  /** Il verso del rapporto preso dalla lettura, per i documenti di un rapporto. */
  verso: 'vendita' | 'acquisto' | 'nessuno';
  perche: string;
}

export interface ProgramMappingStateDto {
  software: string;
  lettura: string;
  etichetta: string;
  tipo_archivio: string;
  colonne: string[];
  mappatura: ProgramMappingDto | null;
  fonte: 'motore' | 'persona' | null;
  esito: { righe: number; voci: number; senza_tipo: number; errore: string | null } | null;
  applicata_il: string | null;
}

/** Pulizia dei dati di un'azienda con le regole dell'ADR 0192: profili, formati, nomi, doppioni; poi la rilettura. */
export interface DataCleanupStateDto {
  fase: 'nessuna' | 'in_corso' | 'finito' | 'errore';
  esito: {
    /** Voci con valori corretti o tolte perché il nome non è un nome. */
    valori: number;
    /** Doppioni uniti (per identità e soggetti). */
    unite: number;
    /** Tipi rimessi tra quelli da rileggere. */
    da_rileggere: number;
  } | null;
  errore: string | null;
  aggiornato_il: string | null;
}

/** Le sezioni di un livello del gestionale (ADR 0210 § 8): per area del menu, i tipi i cui dati stanno lì. */
export interface ScopeAreaDto {
  area: string;
  nome: string;
  tipi: Array<{ chiave: string; nome: string; attribuito?: boolean }>;
}

/** Dove stanno i dati dei tipi composti (ADR 0210 § 4): il livello dell'azienda e quello di ogni sede. */
export interface ModelScopesDto {
  azienda: ScopeAreaDto[];
  sedi: Array<{ id: string; nome: string; codici: string[]; aree: ScopeAreaDto[] }>;
  /** Tipi composti prima dell'ambito (null): si vedono dopo una nuova composizione. */
  senza_ambito: number;
}

/**
 * Un valore sospeso (ADR 0211 passo 3, dall'ADR 0198): arrivato fuori formato (`da_correggere`, non registrato) o
 * leggibile in più modi (`da_confermare`, registrato nella lettura più probabile), con il motivo e da dove è arrivato.
 */
export interface PendingValueDto {
  id: string;
  tipo: string;
  rif: string;
  nome: string | null;
  attributo: string;
  grezzo: string;
  formato: string;
  esito: 'da_correggere' | 'da_confermare';
  motivo: string;
  letture: unknown[];
  fonti: Array<{ fonte: string; il: string }>;
  stato: 'aperto' | 'corretto' | 'confermato' | 'scartato' | 'superato';
  valore_attuale: unknown;
  /** Si può confermare com'è: solo per i formati senza un controllo matematico (non IBAN, codice fiscale, P.IVA). */
  confermabile: boolean;
}

export interface PendingValuesDto {
  sospesi: PendingValueDto[];
  per_tipo: Array<{ tipo: string; aperti: number }>;
}

/**
 * Le voci lette che aspettano il loro genitore (ADR 0238 § 3): il movimento senza il conto. Non sono registrate; entrano
 * da sole quando il genitore c'è. `genitori` sono i genitori possibili, con il nome letto (null se la lettura non lo dice).
 */
export interface WaitingEntitiesDto {
  voci: Array<{ tipo: string; rif: string; nome: string | null; genitori: Array<{ significato: string; verso: string; nome: string | null }>; fonte: string; il: string }>;
  per_tipo: Array<{ tipo: string; voci: number }>;
}

/** La decisione della persona: correggere con un valore nuovo, confermare una lettura o il valore com'è, scartare. */
export const PendingDecisionSchema = z.discriminatedUnion('decisione', [
  z.object({ decisione: z.literal('corretto'), valore: z.union([z.string().trim().min(1).max(2000), z.number(), z.boolean()]) }),
  z.object({ decisione: z.literal('confermato'), valore: z.union([z.string().trim().min(1).max(2000), z.number(), z.boolean()]).optional() }),
  z.object({ decisione: z.literal('scartato') }),
]);
export type PendingDecision = z.infer<typeof PendingDecisionSchema>;

/** Le aggiunte da decidere (ADR 0195 § 6, ADR 0211 passo 3): tipi e attributi con il segno «aggiunto». */
export interface AdditionsDto {
  tipi: Array<{ tipo: string; etichetta: string; il: string; prove: string[]; dalla_libreria: boolean }>;
  attributi: Array<{ tipo: string; attributo: string; etichetta: string; il: string; fonte: string }>;
}

/**
 * Accetta: i tipi indicati con i loro attributi e le loro voci aggiunti fino a `fino` («Accetta tutto» della sezione:
 * ciò che la persona vedeva), oppure solo un attributo o solo alcune voci di un tipo.
 */
export const AdditionsAcceptSchema = z.object({
  tipi: z.array(z.string().regex(/^[a-z][a-z0-9_]{0,62}$/)).min(1).max(200),
  fino: z.string().datetime().optional(),
  attributo: z.string().max(100).optional(),
  rif: z.array(z.string().min(1).max(200)).max(1000).optional(),
});
export type AdditionsAccept = z.infer<typeof AdditionsAcceptSchema>;

/** Scarta un tipo, un attributo o una voce aggiunti: lo scarto resta e una lettura non lo ripropone. */
export const AdditionDiscardSchema = z.discriminatedUnion('cosa', [
  z.object({ cosa: z.literal('tipo'), tipo: z.string().min(1).max(63) }),
  z.object({ cosa: z.literal('attributo'), tipo: z.string().min(1).max(63), nome: z.string().min(1).max(100) }),
  z.object({ cosa: z.literal('voce'), tipo: z.string().min(1).max(63), nome: z.string().min(1).max(200) }),
]);
export type AdditionDiscard = z.infer<typeof AdditionDiscardSchema>;

/** Una voce in un collegamento di pagamento o incasso (ADR 0245). */
export interface SettlementEntityDto {
  tipo: string;
  rif: string;
  nome: string | null;
  data: string | null;
  importo: string | null;
}

/**
 * Pagamento, incasso o giroconto (ADR 0245): il movimento e ciò che salda (o l'altro movimento del giroconto), con
 * l'importo che copre. Certo o proposto dalla regola, confermato o scartato dalla persona.
 */
export interface SettlementDto {
  id: string;
  movimento: SettlementEntityDto;
  cosa: SettlementEntityDto;
  tipo: 'pagamento' | 'incasso' | 'giroconto';
  importo: string;
  origine: 'regola' | 'persona';
  regola: string | null;
  perche: string | null;
  stato: 'certo' | 'proposto' | 'confermato' | 'scartato';
  deciso_da: string | null;
  deciso_il: string | null;
}

export interface SettlementsDto {
  collegamenti: SettlementDto[];
}

/** La persona conferma una proposta o scarta (toglie) un collegamento; uno scartato non torna. */
export const SettlementDecisionSchema = z.object({ decisione: z.enum(['confermato', 'scartato']) });
export type SettlementDecision = z.infer<typeof SettlementDecisionSchema>;

/** La storia dei campi di una voce (ADR 0248 § 4): ogni valore con il giorno a cui si riferisce e la fonte, dal più recente. */
export interface ValueHistoryDto {
  /** `citazione`: il passo da cui viene il valore; `grezzo`: com'era scritto, se diverso (ADR 0251 § 2). */
  storia: Array<{ attributo: string; valore: unknown; data: string; fonte: string; citazione?: string | null; grezzo?: string | null }>;
}
